import * as React from "react";
import TooltipOptions from "@bizform/primereact/components/tooltip/TooltipOptions";
import { Static } from "@bizform/type/decorator/Static.decorator";
import {
    BaseStateModel,
    ComponentElement,
    ComponentElementDisplayMode,
    ComponentElementStatic,
    ComponentEventType
} from "@bizform/type/model/component_element/ComponentElement.model";
import {
    ComponentCategory,
    ComponentElementManifest,
    ComponentElementProperty,
    ComponentElementPropertyType
} from "@bizform/type/model/component_element/ComponentElementManifest.model";
import { CalendarSelectionMode, TooltipEvent } from "@bizform/type/model/data_set/LookUp.model";
import { DataType } from "@bizform/type/model/model/DataType.model";
import {
    AccessibilityDate,
    Calendar,
    CalendarProps,
    ComponentHelper,
    DateHelper,
    DateMetaData,
    Debug,
    DomHelper,
    FunctionHelper,
    LocaleSettings,
    ObjectHelper
} from "@bizform/helper/dist/js/bizflow/helper.min";
import { ComponentElementClass, ReactComponentElementInit } from "@bizform/core/dist/js/bizflow/core.min";
import { InputMaskDate } from "../../../misc/addon/inputmask/InputMaskDate";
import { AbstractComponentElement } from "../../AbstractComponentElement";
import { I18n } from "../../../misc/model/I18n.model";


let ___calendar;

type Props = CalendarProps & {};

type WrappedCompState = {
    id?: string;
    tabindex?: number;
    name?: string;
    value?: Date;
    viewDate?: Date;
    style?: object;
    className?: string;
    inline?: boolean;
    selectionMode?: string;
    inputId?: string;
    inputStyle?: object;
    inputClassName?: string;
    required?: boolean;
    readOnlyInput?: boolean;
    allowToType?: boolean;
    disabled?: boolean;
    placeholder?: string;
    showIcon?: boolean;
    icon?: string;
    showOnFocus?: boolean;
    numberOfMonths?: number;
    view?: string;
    touchUI?: boolean;
    showTime?: boolean;
    timeOnly?: boolean;
    showSeconds?: boolean;
    hourFormat?: string;
    stepHour?: number;
    stepMinute?: number;
    stepSecond?: number;
    shortYearCutoff?: string;
    hideOnDateTimeSelect?: boolean;
    locale?: LocaleSettings;
    dateFormat?: string;
    simpleDateFormat?: {input?: string, view?: string};
    panelStyle?: object;
    panelClassName?: string;
    printStyle?: object;
    printClassName?: string;
    monthNavigator?: boolean;
    yearNavigator?: boolean;
    disabledDates?: Date[];
    disabledDays?: number[];
    minDate?: Date;
    maxDate?: Date;
    maxDateCount?: number;
    showOtherMonths?: boolean;
    selectOtherMonths?: boolean;
    showButtonBar?: boolean;
    todayButtonClassName?: string;
    clearButtonStyleClass?: string;
    closeButtonStyleClass?: string;
    autoZIndex?: boolean;
    baseZIndex?: number;
    appendTo?: any;
    tooltipAttribute?: TooltipOptions & {text?: string};
    yearRange?: string;

    dateTemplate?(dateMeta: DateMetaData): React.ReactNode;
    headerTemplate?(): React.ReactNode;
    footerTemplate?(): React.ReactNode;
    onFocus?(event: Event): void;
    onBlur?(event: Event): void;
    onInput?(event: Event): void;
    onSelect?(e: ComponentEventType): void;
    onChange?(e: ComponentEventType): void;
    onTodayButtonClick?(event: Event): void;
    onClearButtonClick?(event: Event): void;
    onCloseButtonClick?(event: Event): void;
    onViewDateChange?(e: ComponentEventType): void;
};

type WrappedFormState = {
    onChange?: string;
};

type State = BaseStateModel & {
    wrappedCompState: WrappedCompState;
    wrappedFormState: WrappedFormState;
};

/**
 * @since 0.0.1
 * @author Hwansoon Park
 * @class BfTimePicker
 * @description BfTimePicker component element
 */
@ComponentElementClass()
@Static<ComponentElementStatic<Date>>()
export class BfTimePicker extends AbstractComponentElement<Props, State> {
    /* manifest */
    static readonly componentElementManifest: ComponentElementManifest = {
        uniqueId: "_yzQG8isZgU0F5JuGKV2A2D1XEcKyGSr",
        category: ComponentCategory.CALENDAR,
        label: I18n.calendar.timePicker.label,
        description: I18n.calendar.timePicker.label,
        icon: "",
        dataType: [DataType.TIME, DataType.UTCTIME],
        property: null
    };

    /* for React */
    props: Props;
    state: State;

    private _calendar: any;
    private _inputMask: any;
    private _rValue: string;
    private _tooltip: Array<any> = [];
    private _timer: NodeJS.Timer;
    private _subscriptionNull: boolean;

    // shouldComponentUpdate
    private _preDisplayMode: ComponentElementDisplayMode;
    private _preValue: string;
    private _attribute: boolean;
    private _preAttribute: boolean;

    constructor(props?: Props) {
        super(props);

        this.state = {
            show: false,
            displayMode: ComponentElementDisplayMode.INIT,
            wrappedCompState: {},
            wrappedFormState: {}
        };

        this._preValue = null;
        this._preAttribute = false;
        this._attribute = false;

        this._onChangeEvent = this._onChangeEvent.bind(this);
        this._setRef = this._setRef.bind(this);
        this._setRefInputMask = this._setRefInputMask.bind(this);
        this.subscriptionResize = this.subscriptionResize.bind(this);
        this.closeOverlay = this.closeOverlay.bind(this);
        this.alignPanel = this.alignPanel.bind(this);
        if (!___calendar) {
            ___calendar = {};
        }
    }

    @ReactComponentElementInit(BfTimePicker)
    static init(): ComponentElement<Date> {
        return;
    }

    focus(): void {
        if (this.accessibility) {
            this._calendar.validationFocus();
        } else if (this._calendar && this._calendar.showOverlay) {
            this._calendar.inputElement.focus();
            this._calendar.showOverlay();
        }
    }

    setProperty(property: ComponentElementProperty): void {
        try {
            this._attribute = !this._attribute;
            this._removeCustom();
            const newState = ComponentHelper.createStateWithProperties<State, ComponentEventType>(
                property,
                this);
            const newProps = newState.wrappedCompState;
            newProps.showIcon = true;

            const defaultDateFormatInput = DateHelper.getDefaultFormatByType(ComponentElementPropertyType.FORMATTIME);
            const defaultDateFormatView = DateHelper.getDefaultFormatByType(ComponentElementPropertyType.FORMATTIME);
            if (!newProps.simpleDateFormat.input || newProps.simpleDateFormat.input === "") {
                newProps.simpleDateFormat = {
                    input: !newProps.simpleDateFormat.input || newProps.simpleDateFormat.input === "" ? defaultDateFormatInput : newProps.simpleDateFormat.input,
                    view: !newProps.simpleDateFormat.view || newProps.simpleDateFormat.view === "" ? defaultDateFormatView : newProps.simpleDateFormat.view
                };
            }

            if (newProps.simpleDateFormat &&  newProps.simpleDateFormat.view) {
                newProps.showSeconds = newProps.simpleDateFormat.view.indexOf("s") > -1 ? true : false;
                newProps.hourFormat = newProps.simpleDateFormat.view.indexOf("a") > -1 ? "12" : "24";
            }
            this.setState(newState);
        } catch (e) {
            this.emitDidCatch(e);
        }
    }

    emit(value: Date): void {
    }

    emitFormState(value: string): void {
    }

    subscription(value: Date): void {
        const newState = {
            wrappedCompState: {
                ...this.state.wrappedCompState,
                value: null
            }
        };
        if (!ComponentHelper.isNull(value)) {
            value = typeof value === "string" ? null : new Date((value as Date).toString());
        }
        if (!value) {
            this._subscriptionNull = true;
        }
        if (value && value.toString() === "Invalid Date") {
            value = null;
            if (this._inputMask && this._inputMask.input) {
                this._inputMask.input.value = "";
            }
        }
        newState.wrappedCompState.value = value;
        this.setState(newState);
    }

    subscriptionResize(): void {
        if (___calendar) {
            for (const panelName in ___calendar) {
                const panel = ___calendar[panelName];
                if (panel._timer) {
                    clearTimeout(panel._timer);
                }
                panel._timer = setTimeout(panel.alignPanel, 100);
            }
        }
    }

    initDone(): void {
        ___calendar[this.elementId] = this;
    }

    destroy(): void {
        this.destroyDone();
    }

    destroyDone(): void {
    }

    shouldComponentUpdate(nextProps: Readonly<Props>, nextState: Readonly<State>, nextContext: any): boolean {
        if (!this.displayMode || !this.state.wrappedCompState) {
            return false;
        }
        let result = false;
        if (this._preDisplayMode !== this.displayMode || this._preAttribute !== this._attribute) {
            result = true;
        } else {
            result = this.state.wrappedCompState.value !== nextState.wrappedCompState.value;
        }
        if (this._inputMask && this._inputMask.input) {
            if (!result) {
                result = this._inputMask.input.value !== this._preValue;
            }
            this._preValue = this._inputMask.input.value;
        }
        this._preDisplayMode = this.displayMode;
        this._preAttribute = this._attribute;
        return result;
    }

    componentDidUpdate(): void {
        if (this.accessibility) {
            return;
        }
        let hasTooltip = this._tooltip.length ? true : false;
        if (this._calendar) {
            if (this._calendar.container) {
                if (this.state.wrappedCompState.value) {
                    this._calendar.inputElement.value = DateHelper.formatter(this.state.wrappedCompState.value as Date, this.state.wrappedCompState.simpleDateFormat.view);
                }
                if (this.state.wrappedCompState.tabindex !== this.___tabindex) {
                    ComponentHelper.tabindex(this.displayMode, this._calendar.inputElement, this.state.wrappedCompState.tabindex);
                }

                if ((this.state.wrappedCompState.tooltipAttribute && this.state.wrappedCompState.tooltipAttribute.event === TooltipEvent.HOVER) ||
                    (!this.state.wrappedCompState.allowToType && this.state.wrappedCompState.tooltipAttribute && this.state.wrappedCompState.tooltipAttribute.event === TooltipEvent.FOCUS)
                ) {
                    if (!hasTooltip) {
                        ComponentHelper.tooltip(this._calendar.inputElement, this.state.wrappedCompState.tooltipAttribute, this._tooltip);
                    }
                }
            }

            this.alignPanel();
        }
        if (this._inputMask && this._inputMask.input) {
            if (this.state.wrappedCompState.tabindex !== this.___tabindex) {
                ComponentHelper.tabindex(this.displayMode, this._inputMask.input, this.state.wrappedCompState.tabindex);
            }
            if (!this.state.wrappedCompState.allowToType ||
                this.state.wrappedCompState.tooltipAttribute && this.state.wrappedCompState.tooltipAttribute.event === TooltipEvent.HOVER ||
                (this.state.wrappedCompState.allowToType && this.state.wrappedCompState.tooltipAttribute && this.state.wrappedCompState.tooltipAttribute.event === TooltipEvent.FOCUS)
            ) {
                if (!hasTooltip) {
                    ComponentHelper.tooltip(this._inputMask.input, this.state.wrappedCompState.tooltipAttribute, this._tooltip);
                }
            }
        }
        this.___tabindex = this.state.wrappedCompState.tabindex;
    }

    componentWillUnmount(): void {
        this._removeCustom();
        this._calendar = null;
        this._inputMask = null;
        this._timer = null;
        delete ___calendar[this.elementId];
    }

    componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
        this.emitDidCatch(error);
    }

    emitSubscriptionReject(): void {
    }

    emitDidCatch(error: Error | any): void {
        Debug.console.error(error);
    }

    closeOverlay() {
        if (this._calendar && this._calendar.hideOverlay) {
            this._calendar.hideOverlay();
        }
    }

    alignPanel() {
        if (this._calendar && this._calendar.alignPanel) {
            this._calendar.alignPanel();
        }
    }

    private _onChangeEvent(e: ComponentEventType): void {
        if (!this.accessibility && !e.value && this._inputMask && this._inputMask.input && e.originalEvent.target !== this._inputMask.input) {
            this._inputMask.input.value = this._rValue;
            this._rValue = null;
        }
        const newProps = {
            wrappedCompState: {
                ...this.state.wrappedCompState,
                value: e.value
            }
        };

        this.setState(newProps, this._callbackSetState);
    }

    private _callbackSetState(): void {
        let value;
        if (!this.accessibility) {
            value = this.state.wrappedCompState.value ? this.state.wrappedCompState.value : (this._inputMask.input.value.replace(/[^0-9]*/g, "") === "" ? "" : this._inputMask.input.value);
        } else {
            value = this.state.wrappedCompState.value;
        }
        this.emit(value);
        if (this.state.wrappedFormState && this.state.wrappedFormState.onChange) {
            this.emitFormState(this.state.wrappedFormState.onChange);
        }
    }

    render(): React.ReactNode {
        const {
            show,
            displayMode
        } = this.state;

        if (!show) {
            return null;
        } else {
            if (this.accessibility && displayMode !== ComponentElementDisplayMode.PRINT) {
                return this._render4Edit();
            }
            switch (displayMode) {
                case ComponentElementDisplayMode.EDIT:
                    return this._render4Edit();
                case ComponentElementDisplayMode.READONLY:
                    return this._render4ReadOnly();
                case ComponentElementDisplayMode.DISABLED:
                    return this._render4Disabled();
                case ComponentElementDisplayMode.PRINT:
                    return this._render4Print();
                default:
                    return null;
            }
        }
    }

    private _inputMaskOnComplete(e): void {
        const value = DateHelper.formatter(new Date(), "yyyy/MM/dd") + " " + this._inputMask.input.value;

        this._rValue = null;
        if (value) {
            const dateValue = new Date(value);
            if (!dateValue || dateValue.toString() === "Invalid Date") {
                this._rValue = this._inputMask.input.value;
                this.state.wrappedCompState.onChange({
                    originalEvent: e,
                    value: null
                });
                return;
            }
        }

        this.state.wrappedCompState.onChange({
            originalEvent: e,
            value: DateHelper.convertStringToDateByInputFormat(value, "yyyy/MM/dd " + this.state.wrappedCompState.simpleDateFormat.input)
        });
        this.closeOverlay();
    }

    private async _inputMaskOnFocusout(e): Promise<void> {
        if (this._inputMask && this._inputMask.input) {
            let value = DateHelper.formatter(new Date(), "yyyy/MM/dd") + " " + this._inputMask.input.value;
            try {
                // error check
                if (this._inputMask.getUnmaskedValue() !== "") {
                    const formatDate = DateHelper.convertStringToDateByInputFormat(value, "yyyy/MM/dd " + this.state.wrappedCompState.simpleDateFormat.input);
                    if (!formatDate) {
                        throw new Error();
                    }
                }

                value = DateHelper.formatter(new Date(), "yyyy/MM/dd") + " " + this._inputMask.input.value;
                this._inputMask.input.style.display = "none";
            } catch (e) {
                if (this.state.wrappedCompState.value !== null && this.state.wrappedCompState.value !== undefined) {
                    this.state.wrappedCompState.onChange({
                        originalEvent: e,
                        value: null
                    });
                }
            }
        }
        await FunctionHelper.timeout(100);
        if (this._calendar && this._calendar.inputElement) {
            this._calendar.inputElement.disabled = false;
        }
    }

    private async _calendarInputFocus(e): Promise<void> {
        if (this.state.displayMode === ComponentElementDisplayMode.EDIT) {
            if (!this.state.wrappedCompState.allowToType) {
                if (this.state.wrappedCompState.showOnFocus) {
                    this._calendar.showOverlay();
                }
            } else {
                this._calendar.inputElement.blur();
                this._calendar.inputElement.disabled = true;
                await FunctionHelper.timeout(100);
                if (this._inputMask && this._inputMask.input) {
                    this._inputMask.input.style.display = "block";
                    this._inputMask.input.focus();
                }
            }
        }
    }

    private _inputMaskOnFocus(e): void {
        if (this.state.displayMode === ComponentElementDisplayMode.EDIT) {
            this._calendar.inputElement.disabled = true;
            if (this.state.wrappedCompState.showOnFocus) {
                this._calendar.showOverlay();
            }
        } else {
            this._inputMask.input.style.display = "none";
        }
    }

    private _render(newProps: WrappedCompState): React.ReactNode {
        delete(newProps["printClassName"]);
        delete(newProps["printStyle"]);
        if (this.accessibility) {
            return (
                <AccessibilityDate
                    tabindex={newProps.tabindex}
                    key={"Calendar_" + this.elementId}
                    ref={this._setRef}
                    id={this.elementId}
                    value={newProps.value}
                    displayMode={this.displayMode}
                    locale={newProps.locale}
                    simpleDateFormat={newProps.simpleDateFormat}
                    hourFormat={newProps.hourFormat}
                    onChange={newProps.onChange}
                    tooltipAttribute={newProps.tooltipAttribute}
                />
            );
        } else {
            delete newProps.tooltipAttribute;
            return (
                <div style={{ "position": "relative" }}>
                    <Calendar
                        {...newProps}
                        key={"Calendar_" + this.elementId}
                        inputId={this.elementId}
                        selectionMode={CalendarSelectionMode.SINGLE}
                        showOnFocus={false}
                        onFocus={(e) => this._calendarInputFocus(e)}
                        showTime={true}
                        timeOnly={true}
                        hideOnDateTimeSelect={false}
                        readOnlyInput={!newProps.allowToType}
                        style={newProps.showIcon === true ? {
                            ...newProps.style,
                            "width": "calc(100% - 30px)"
                        } : { ...newProps.style, "width": "100%" }}
                        appendTo={DomHelper.getForOverLayDom()}
                        ref={this._setRef}
                    />
                    {this._renderInputMask(newProps)}
                </div>
            );
        }
    }

    private _renderInputMask(newProps: WrappedCompState) {
        const style = newProps.showIcon === true ? {"width": "calc(100% - 30px)"} : {"width": "100%"};
        let value = "";
        if (newProps.value && this._inputMask && this._inputMask.input) {
            value = DateHelper.formatter(newProps.value, newProps.simpleDateFormat.input);
            this._inputMask.input.style.display = "none";
        } else {
            if (this._inputMask && this._inputMask.input) {
                if (this._subscriptionNull) {
                    this._subscriptionNull = false;
                    value = null;
                    this._inputMask.input.value = null;
                    this._inputMask.input.style.display = "none";
                } else {
                    value = this._inputMask.input.value;
                }
            }
        }
        return (
            <InputMaskDate
                key={"InputMaskDate_" + this.elementId}
                autoClear={false}
                style={{"position": "absolute", "top": "0", "left": "0", "display": "none", ...style}}
                placeholder={newProps.placeholder}
                mask={newProps.simpleDateFormat.input}
                value={value}
                readOnly={this.state.displayMode === ComponentElementDisplayMode.READONLY || !newProps.allowToType}
                onComplete={(e) => this._inputMaskOnComplete(e)}
                onChange={(e) => this._inputMaskOnComplete(e)}
                onInputFocus={(e) => this._inputMaskOnFocus(e)}
                onFocusout={(e) => this._inputMaskOnFocusout(e)}
                ref={this._setRefInputMask}
            />
        );
    }

    private _render4Edit(): React.ReactNode {
        const newProps = {
            ...this.state.wrappedCompState
        };

        return this._render(newProps);
    }

    private _render4ReadOnly(): React.ReactNode {
        const newProps = {
            ...this.state.wrappedCompState,
            allowToType : false,
            showOnFocus: false,
            readOnly: true
        };

        return this._render(newProps);
    }

    private _render4Disabled(): React.ReactNode {
        const newProps = {
            ...this.state.wrappedCompState,
            disabled: true
        };

        return this._render(newProps);
    }

    private _render4Print(): React.ReactNode {
        let value: any = "";
        if (this.state.wrappedCompState.value) {
            value = DateHelper.formatter(this.state.wrappedCompState.value, this.state.wrappedCompState.simpleDateFormat.view);
        }
        return (
            <div ref={this._setRef} style={this.state.wrappedCompState ? {...this.state.wrappedCompState.printStyle} : null} className={`bf-print-mode ${this.state.wrappedCompState ? this.state.wrappedCompState.printClassName : null}`}>{value}</div>
        );
    }

    private _setRef(ref) {
        if (ref) {
            if (this._tooltip.length) {
                ComponentHelper.unbindTooltip(this._tooltip);
            }
            this._calendar = ref;
            if (this._calendar && this._calendar.container) {
                const button = this._calendar.container.querySelector("button");
                if (this.state.wrappedCompState.allowToType) {
                    button.setAttribute("tabindex", "-1");
                } else {
                    button.removeAttribute("tabindex");
                }
            }
            this.readyComponent();
        }
    }

    private _setRefInputMask(ref) {
        if (ref) {
            if (this._tooltip.length) {
                ComponentHelper.unbindTooltip(this._tooltip);
            }
            this._inputMask = ref;
        }
    }

    private _removeCustom(): void {
        if (this.accessibility) {
            return;
        }
        ComponentHelper.unbindTooltip(this._tooltip);
    }
}
